import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import cn from 'classnames';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import ExclusiveButton from '@wix/da-ds/pkg/Buttons/ExclusiveButton';
import DeviationDumbView from '../../../DeviationViews/Dumbo';
import Surface from '@wix/da-ds/pkg/Surface';
import { ThemeSurface } from '@wix/da-react-context/pkg/constants';
import UserAvatar from '../../../User/Avatar';
import UserLink from '../../../User/Link/UserLink';
import { formatDollars } from '../../../utils/formatting';
import ExclusiveSpiderWeb from '../CommentBoxContentWrapper/ExclusiveSpiderWeb';
import ExclusiveAvatarButton from '../ExclusiveAvatarButton';
import { useDeviationMatureFilter } from '../../../utils/hooks/useDeviationMatureFilter';
import { getIsMatureBlock } from '../../../Moderation/redux/selectors';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  BiData,
  BiContextProvidedValue,
  ItemClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ResourceType } from '../../../types/resource';
import { useExtendedDeviation } from '../../../utils/hooks/useExtendedDeviation';

import s from './ExclusiveAvatarOverlay.scss';

export interface Props {
  deviation: PapiDeviation;
  commentedByUser?: PapiUser;
  className?: string;
}

const ExclusiveAvatarOverlay: React.FC<Props> = ({
  deviation: deviationProp,
  commentedByUser,
  className,
}) => {
  const { t } = useTranslation();

  const extendedDeviation = useExtendedDeviation(deviationProp);
  const deviation = extendedDeviation || deviationProp;

  const adoptable = deviation.adoptable;
  const isForSale = deviation.adoptable?.status === 'onsale';
  const fixedPrice = deviation.adoptable?.fixedPrice?.fixedPrice || 0;

  const { showBlocked, displayReason } = useDeviationMatureFilter(deviation);
  const isMatureBlocked = showBlocked && getIsMatureBlock(displayReason);
  const logBiEvent = useBiLogger();

  const handleViewDeviation = () => {
    const baseSectionName = 'exclusive_avatar_popup';
    // Log item_click (evid 100) for navigation to the exclusive deviation
    logBiEvent(
      BiData<ItemClickBiEvent>({
        evid: 100,
        sectionname:
          isForSale && fixedPrice
            ? `${baseSectionName}_purchase`
            : baseSectionName,
        page_typeid: BiContextProvidedValue,
        page_itemid: BiContextProvidedValue,
        typeid: ResourceType.DEVIATION,
        itemid: deviation.deviationId,
      })
    );

    if (deviation.url) {
      window.location.assign(deviation.url);
    }
  };

  return (
    <Surface type={ThemeSurface.TERTIARY} className={cn(className, s['root'])}>
      <ExclusiveSpiderWeb className={s['exclusive-spider-web']} />
      <div className={s['header']}>
        <div className={s['avatar-info']}>
          {adoptable && (
            <div className={s['avatar']} onClick={handleViewDeviation}>
              <UserAvatar user={adoptable?.owner} size={24} />
            </div>
          )}
          <div className={s['ownership-text']}>
            <span className={s['label']}>
              {t('commentBox.exclusiveAvatarOverlay.avatarOwnedBy')}
            </span>
            {adoptable?.owner && (
              <UserLink className={s['username']} user={adoptable?.owner}>
                {adoptable?.owner?.username}
              </UserLink>
            )}
          </div>
        </div>
      </div>

      <div className={s['main-content']}>
        <div className={s['deviation-image']} onClick={handleViewDeviation}>
          <DeviationDumbView
            deviation={deviation}
            width={171}
            height={171}
            allowPreviewUsage={true}
            useBackgroundColor={true}
            greenLikeCrop={true}
          />
        </div>

        {isMatureBlocked && (
          <div className={s['mature-label']}>
            {t('commentBox.exclusiveAvatarOverlay.matureLabel')}
          </div>
        )}

        <div className={s['deviation-info']}>
          <h3 className={s['title']}>{deviation.title}</h3>
          <p className={s['description']}>
            <Trans
              i18nKey="commentBox.exclusiveAvatars.overlay.description"
              components={[
                <a
                  key="link"
                  href={t(
                    'commentBox.exclusiveAvatars.greetingsModal.learnMoreUrl'
                  )}
                  className={s['description-link']}
                />,
              ]}
            />
          </p>
        </div>
      </div>

      <div className={s['footer']}>
        {isForSale && fixedPrice ? (
          <ExclusiveButton
            className={s['buy-button']}
            priority="primary"
            onClick={handleViewDeviation}
          >
            {`Buy for ${formatDollars(fixedPrice, { useGrouping: true })}`}
          </ExclusiveButton>
        ) : (
          <ExclusiveAvatarButton onClick={handleViewDeviation}>
            {t('commentBox.exclusiveAvatarOverlay.viewDeviation')}
          </ExclusiveAvatarButton>
        )}
        {commentedByUser &&
          adoptable?.owner &&
          commentedByUser.userId !== adoptable.owner.userId && (
            <div className={s['header']}>
              <div className={s['avatar-info']}>
                <div className={s['ownership-text']}>
                  <span className={s['label']}>Commented by</span>
                  <UserLink className={s['username']} user={commentedByUser}>
                    {commentedByUser.username}
                  </UserLink>
                </div>
              </div>
            </div>
          )}
      </div>
    </Surface>
  );
};

ExclusiveAvatarOverlay.displayName = 'ExclusiveAvatarOverlay';

export default ExclusiveAvatarOverlay;
